import { useEffect, useRef, type JSX } from 'react';
import { mountLivingBackdrop, type LivingBackdrop as Handle } from './living-backdrop';

/**
 * M0 "First Breath" — React host for the pre-auth living backdrop. Mounts the self-contained Pixi
 * scene (see {@link mountLivingBackdrop}) behind the auth card so the world is already MOVING before
 * the newcomer touches anything (no blank/static/"Loading…" frame). Pure decoration:
 *
 *  • `aria-hidden` + `pointer-events: none` → never in the tab order, never steals form clicks.
 *  • `data-living-backdrop` → the dev/e2e signal the "manual testing agent" keys off to assert the
 *    world is alive (paired with `window.__duckFirstBreath.backdropAlive`).
 *  • respects `prefers-reduced-motion` inside the scene (static, correct frame; no motion).
 *
 * StrictMode is intentionally off app-wide (see main.tsx), so this mounts once; the cleanup still
 * tears the Pixi app down fully on unmount/route-change.
 */
export function LivingBackdrop(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let handle: Handle | null = null;
    let cancelled = false;

    void mountLivingBackdrop(el).then((h) => {
      if (cancelled) {
        h?.destroy();
        return;
      }
      handle = h;
    });

    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, []);

  return (
    <div
      ref={ref}
      data-living-backdrop="true"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    />
  );
}
